export type ConstraintMarkerSettingsState = {
  markerScalePercent: number;
  lineWidthPercent: number;
  anchorSpheresVisible: boolean;
  tendonParsedAppearance: boolean;
};

export const CONSTRAINT_MARKER_SETTINGS_EVENT = "constraint-marker-settings-changed";
export const CONSTRAINT_MARKER_SCALE_DEFAULT_PERCENT = 100;
export const CONSTRAINT_MARKER_LINE_WIDTH_DEFAULT_PERCENT = 100;
export const CONSTRAINT_MARKER_SCALE_MIN_PERCENT = 10;
export const CONSTRAINT_MARKER_SCALE_MAX_PERCENT = 200;
export const CONSTRAINT_MARKER_LINE_WIDTH_MIN_PERCENT = 10;
export const CONSTRAINT_MARKER_LINE_WIDTH_MAX_PERCENT = 200;
export const CONSTRAINT_MARKER_SETTINGS_STEP_PERCENT = 5;

const STORAGE_KEY = "critter.constraintMarkerSettings";

function clampPercent(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}

export function clampConstraintMarkerScalePercent(value: number): number {
  return clampPercent(
    value,
    CONSTRAINT_MARKER_SCALE_MIN_PERCENT,
    CONSTRAINT_MARKER_SCALE_MAX_PERCENT,
    CONSTRAINT_MARKER_SCALE_DEFAULT_PERCENT
  );
}

export function clampConstraintMarkerLineWidthPercent(value: number): number {
  return clampPercent(
    value,
    CONSTRAINT_MARKER_LINE_WIDTH_MIN_PERCENT,
    CONSTRAINT_MARKER_LINE_WIDTH_MAX_PERCENT,
    CONSTRAINT_MARKER_LINE_WIDTH_DEFAULT_PERCENT
  );
}

const defaultState = (): ConstraintMarkerSettingsState => ({
  markerScalePercent: CONSTRAINT_MARKER_SCALE_DEFAULT_PERCENT,
  lineWidthPercent: CONSTRAINT_MARKER_LINE_WIDTH_DEFAULT_PERCENT,
  anchorSpheresVisible: true,
  tendonParsedAppearance: true,
});

let currentState: ConstraintMarkerSettingsState = defaultState();

function normalizeState(
  input: Partial<ConstraintMarkerSettingsState>
): ConstraintMarkerSettingsState {
  return {
    markerScalePercent: clampConstraintMarkerScalePercent(
      Number(input.markerScalePercent ?? currentState.markerScalePercent)
    ),
    lineWidthPercent: clampConstraintMarkerLineWidthPercent(
      Number(input.lineWidthPercent ?? currentState.lineWidthPercent)
    ),
    anchorSpheresVisible:
      typeof input.anchorSpheresVisible === "boolean"
        ? input.anchorSpheresVisible
        : currentState.anchorSpheresVisible,
    tendonParsedAppearance:
      typeof input.tendonParsedAppearance === "boolean"
        ? input.tendonParsedAppearance
        : currentState.tendonParsedAppearance,
  };
}

export function readConstraintMarkerSettings(): ConstraintMarkerSettingsState {
  if (typeof window === "undefined") return { ...currentState };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      currentState = normalizeState(JSON.parse(raw));
    }
  } catch {
    currentState = normalizeState(currentState);
  }
  return { ...currentState };
}

export function dispatchConstraintMarkerSettings(
  patch: Partial<ConstraintMarkerSettingsState>
): ConstraintMarkerSettingsState {
  currentState = normalizeState(patch);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    } catch {
      // Best-effort preference persistence; still notify the live UI below.
    }
    window.dispatchEvent(
      new CustomEvent(CONSTRAINT_MARKER_SETTINGS_EVENT, {
        detail: { ...currentState },
      })
    );
  }
  return { ...currentState };
}
